import { Injectable } from '@angular/core';
import { environment } from '@envs/environment';
import { RequestService } from 'clicklease-ui-elements';
import { Observable } from 'rxjs';

import {
    BackupPaymentMethod,
    BackupPaymentMethodToken,
    PaymentSession,
} from '../../interfaces/payment-method';

/**
 * Backup payment method API (OPS-2028).
 *
 * Everything is routed through rose-portal-service — the portal never calls payment-service
 * directly. Endpoints live under the lease so the service can prove lease ownership before
 * anything reaches payment-service.
 */
@Injectable({
    providedIn: 'root',
})
export class PaymentMethodRequestService extends RequestService {

    protected override readonly serviceBaseUrl: string = `${environment.API_URL}leases/`;

    /** Resolves to null when the ROSE has no backup method yet (service answers 204). */
    public getBackupPaymentMethod(leaseKey: string): Observable<BackupPaymentMethod | null> {
        return this.get<BackupPaymentMethod | null>({
            url: `${leaseKey}/payment-method`,
            headers: { 'content-type': 'application/json' }
        });
    }

    /** Opens a PayNearMe tokenize session; the form is prefilled server-side from the ROSE record. */
    public openTokenizeSession(leaseKey: string): Observable<PaymentSession> {
        return this.post<PaymentSession>({
            url: `${leaseKey}/payment-method/session`,
            params: {},
            headers: { 'content-type': 'application/json' }
        });
    }

    /**
     * Stores the tokenized card together with the authorization it was captured under.
     *
     * One request, because rose-portal-service writes both onto the same payment-service row.
     * A separate follow-up call made it reachable for a card to come to rest with no consent
     * recorded whenever that second request failed. Only the wording travels here - the
     * accepted flag is asserted server-side, never by the browser.
     */
    public saveBackupPaymentMethod(
        leaseKey: string,
        token: BackupPaymentMethodToken,
        authorizationDescription: string
    ): Observable<void> {
        return this.post<void>({
            url: `${leaseKey}/payment-method`,
            params: { ...token, authorizationDescription },
            headers: { 'content-type': 'application/json' }
        });
    }
}
